// frontend/src/pages/Finance/CashflowDashboard.jsx
// 🎨 VERSIÓN MEJORADA - DISEÑO MINIMALISTA CONSISTENTE CON DASHBOARD

import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  ButtonGroup,
  Alert,
} from 'reactstrap';
import { cashflowAPI, invoicesAPI } from '../../services/api';
import MonthlyCashflowChart from '../../components/Charts/MonthlyCashflowChart';
import InvoiceViewerModal from '../../components/Modals/InvoiceViewerModal';
import { parseDate } from '../../utils/dates';

const CashflowDashboard = () => {
  const currentYear = new Date().getFullYear();
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [summary, setSummary] = useState(null);
  const [projection, setProjection] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  useEffect(() => {
    loadCashflowData();
  }, [period]);

  const loadCashflowData = async () => {
    try {
      setLoading(true);
      
      if (period === 'year') {
        const annualResponse = await cashflowAPI.getAnnualSummary(currentYear);
        const annualData = annualResponse.data.data || annualResponse.data;
        setSummary(annualData);
        setProjection(annualData.months || []);
      } else {
        const summaryResponse = await cashflowAPI.getSummary(period);
        setSummary(summaryResponse.data.data || summaryResponse.data);

        // Incluir el mes actual y los meses que alcanza el horizonte elegido.
        const projectionResponse = await cashflowAPI.getMonthlyProjection(Math.ceil(period / 30) + 1);
        setProjection(projectionResponse.data.data || []);
      }
      
      const overdueResponse = await cashflowAPI.getOverdue();
      setOverdueInvoices(overdueResponse.data.data || overdueResponse.data.invoices || []);
      
      const pendingResponse = await invoicesAPI.getAll({ status: 'pending' });
      setPendingInvoices(pendingResponse.data.results || pendingResponse.data || []);
      
      setError(null);
    } catch (err) {
      console.error('Error cargando cashflow:', err);
      setError('No se pudieron cargar los datos de cashflow');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    if (!window.confirm('¿Marcar esta factura como pagada?')) return;
    
    try {
      await invoicesAPI.markPaid(invoiceId, {
        payment_date: new Date().toLocaleDateString('en-CA'),
        notes: 'Marcado como pagado desde dashboard'
      });
      
      setSuccess('Factura marcada como pagada exitosamente');
      loadCashflowData();
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error marcando factura como pagada:', err);
      setError('Error al marcar la factura como pagada');
    }
  };

  const viewInvoiceDetail = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setViewerModalOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return parseDate(date).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPeriodLabel = () => {
    if (period === 'year') return `Año ${currentYear}`;
    const labels = {
      30: '30 días',
      60: '60 días',
      90: '90 días',
    };
    return labels[period] || '30 días';
  };

  const isAnnual = period === 'year';
  const today = new Date();
  const projectionEnd = new Date(today);
  if (!isAnnual) projectionEnd.setDate(projectionEnd.getDate() + period);
  const currentMonth = today.toLocaleDateString('es-MX', { month: 'long' });
  const projectionRange = isAnnual
    ? `enero – diciembre ${currentYear}`
    : `${today.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – ${projectionEnd.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`;

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading && !summary) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando dashboard de cashflow...</p>
          </div>
        </Container>
      </>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================
  if (error && !summary) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="alert alert-danger shadow mt-5" role="alert">
            <span className="alert-icon"><i className="ni ni-fat-remove"></i></span>
            <span className="alert-text">{error}</span>
            <button 
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={loadCashflowData}
            >
              <i className="ni ni-curved-next me-2"></i>
              Reintentar
            </button>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
      
      <Container fluid className="py-4">
        {/* ============================================ */}
        {/* HEADER CON FILTROS */}
        {/* ============================================ */}
        <Row className="mb-4">
          <Col md="6">
            <h6 className="text-white mb-0">
              <i className="ni ni-money-coins me-2"></i>
              Cobro y Cashflow - {getPeriodLabel()}
            </h6>
            <p className="text-white text-sm opacity-8 mb-0">
              Monitoreo de flujo de efectivo y estado de cobros
            </p>
          </Col>
          <Col md="6" className="text-end">
            <ButtonGroup size="sm">
              <Button
                color={period === 30 ? 'white' : 'white'}
                outline={period !== 30}
                onClick={() => setPeriod(30)}
              >
                <i className="ni ni-time-alarm me-1"></i>
                30 días
              </Button>
              <Button
                color={period === 60 ? 'white' : 'white'}
                outline={period !== 60}
                onClick={() => setPeriod(60)}
              >
                <i className="ni ni-calendar-grid-58 me-1"></i>
                60 días
              </Button>
              <Button
                color={period === 90 ? 'white' : 'white'}
                outline={period !== 90}
                onClick={() => setPeriod(90)}
              >
                <i className="ni ni-world me-1"></i>
                90 días
              </Button>
              <Button
                color="white"
                outline={period !== 'year'}
                onClick={() => setPeriod('year')}
              >
                <i className="ni ni-calendar-grid-58 me-1"></i>
                Año {currentYear}
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* ALERTAS */}
        {/* ============================================ */}
        {success && (
          <Alert color="success" className="shadow-sm" toggle={() => setSuccess(null)}>
            <span className="alert-icon"><i className="ni ni-like-2"></i></span>
            <span className="alert-text">{success}</span>
          </Alert>
        )}

        {error && (
          <Alert color="danger" className="shadow-sm" toggle={() => setError(null)}>
            <span className="alert-icon"><i className="ni ni-support-16"></i></span>
            <span className="alert-text">{error}</span>
          </Alert>
        )}

        {/* ============================================ */}
        {/* CARDS DE ESTADÍSTICAS */}
        {/* ============================================ */}
        {summary && (
          <Row>
                        {/* Card 1: Por Cobrar Este Mes */}
            <Col xl="3" sm="6" className="mb-xl-0 mb-4">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                          {isAnnual ? `Flujo registrado ${currentYear}` : `Pendiente en ${currentMonth}`}
                        </p>
                        <p className="h5 font-weight-bold mb-0 text-dark">
                          {formatCurrency(isAnnual ? summary.annual_total : summary.por_cobrar_este_mes)}
                        </p>
                        <small className="text-xs text-muted d-block">
                          {isAnnual ? 'Cobrado + pendiente · total con IVA' : 'Total con IVA · vence este mes'}
                        </small>
                        <p className="mb-0 mt-1">
                          <span className="text-sm text-secondary">
                            {isAnnual
                              ? `${(summary.collected_count || 0) + (summary.pending_count || 0)} movimientos del año`
                              : `${summary.por_cobrar_count || 0} facturas pendientes`}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="col-4 text-end">
                      <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                        <i className="ni ni-money-coins text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Card 2: Pagadas Este Mes */}
            <Col xl="3" sm="6" className="mb-xl-0 mb-4">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                          Cobrado en {isAnnual ? currentYear : currentMonth}
                        </p>
                        <p className="h5 font-weight-bold mb-0 text-dark">
                          {formatCurrency(isAnnual ? summary.collected_total : summary.pagadas_este_mes)}
                        </p>
                        <small className="text-xs text-muted d-block">
                          Total con IVA · pagos registrados
                        </small>
                        <p className="mb-0 mt-1">
                          <span className="text-success text-sm font-weight-bolder">
                            {isAnnual ? summary.collected_count : summary.pagadas_count || 0} facturas · {isAnnual ? summary.collection_rate : summary.tasa_cobro || 0}% del flujo
                          </span>
                        </p>
                      </div>
                    </div>


                    <div className="col-4 text-end">
                      <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                        <i className="ni ni-check-bold text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Card 3: Vencidas */}
            <Col xl="3" sm="6" className="mb-xl-0 mb-4">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                          {isAnnual ? `Pendiente ${currentYear}` : 'Cartera vencida'}
                        </p>
                        <p className="h5 font-weight-bold mb-0 text-dark">
                          {formatCurrency(isAnnual ? summary.pending_total : summary.vencidas)}
                        </p>
                        <small className="text-xs text-muted d-block">
                          {isAnnual ? 'Por cobrar durante el año · con IVA' : 'Total con IVA · pago atrasado'}
                        </small>
                        <p className="mb-0 mt-1">
                          <span className={`text-${isAnnual ? 'warning' : ((summary.vencidas_count || 0) > 0 ? 'danger' : 'success')} text-sm font-weight-bolder`}>
                            {isAnnual
                              ? `${summary.pending_count || 0} facturas pendientes`
                              : `${summary.vencidas_count || 0} facturas requieren seguimiento`}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="col-4 text-end">
                      <div className={`icon icon-shape bg-gradient-${isAnnual ? 'warning' : 'danger'} shadow text-center rounded-circle`}>
                        <i className="ni ni-fat-remove text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Card 4: Proyectado */}
            <Col xl="3" sm="6">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                          {isAnnual ? `Vencido ${currentYear}` : `Cobro próximo · ${period} días`}
                        </p>
                        <p className="h5 font-weight-bold mb-0 text-dark">
                          {formatCurrency(isAnnual ? summary.overdue_total : summary.proyectado_proximos_dias)}
                        </p>
                        <small className="text-xs text-muted d-block">
                          {isAnnual ? 'Parte del pendiente que ya se atrasó' : `Total con IVA · ${projectionRange}`}
                        </small>
                        <p className="mb-0 mt-1">
                          <span className={`text-${isAnnual ? 'danger' : 'info'} text-sm font-weight-bolder`}>
                            {isAnnual
                              ? `${summary.overdue_count || 0} facturas vencidas`
                              : `${summary.proyectado_count || 0} facturas por vencer`}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="col-4 text-end">
                      <div className={`icon icon-shape bg-gradient-${isAnnual ? 'danger' : 'info'} shadow text-center rounded-circle`}>
                        <i className="ni ni-chart-bar-32 text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ============================================ */}
        {/* GRÁFICA DE PROYECCIÓN */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0 z-index-2">
              <CardHeader className="pb-0 pt-3 bg-transparent border-0">
                <h6 className="text-capitalize mb-0">
                  <i className="ni ni-chart-bar-32 me-2"></i>
                  {isAnnual ? `Cashflow anual ${currentYear}` : 'Panorama Mensual de Cobros'}
                </h6>
                <p className="text-sm mb-0 mt-2">
                  <span className="font-weight-bold">Cobrado y pendiente</span>{' '}
                  {isAnnual ? 'de enero a diciembre' : 'agrupado por mes de vencimiento'}
                </p>
              </CardHeader>
              <CardBody className="p-3">
                <div className="chart">
                  {projection && projection.length > 0 ? (
                    <MonthlyCashflowChart data={projection} />
                  ) : (
                    <div
                      style={{ height: '300px' }}
                      className="d-flex flex-column align-items-center justify-content-center bg-gradient-light border-radius-lg"
                    >
                      <i className="ni ni-chart-bar-32 text-muted" style={{ fontSize: '3rem' }}></i>
                      <p className="text-muted mb-0 mt-3 text-center">
                        No hay datos suficientes para mostrar la gráfica
                        <br />
                        <small>Genera más facturas para ver la proyección de cashflow</small>
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* FACTURAS VENCIDAS - ATENCIÓN INMEDIATA */}
        {/* ============================================ */}
        {overdueInvoices.length > 0 && (
          <Row className="mt-4">
            <Col lg="12">
              <Card className="shadow border-0 mb-4">
                <CardHeader className="pb-0 border-0">
                  <Row className="align-items-center">
                    <Col md="6">
                      <h6 className="mb-0">
                        <i className="ni ni-fat-remove me-2 text-danger"></i>
                        Facturas Vencidas - Atención Inmediata
                      </h6>
                      <p className="text-xs text-muted mb-0 mt-1">
                        Facturas que requieren seguimiento urgente
                      </p>
                    </Col>
                    <Col md="6" className="text-end">
                      <Badge color="" className="badge-lg bg-gradient-danger shadow-sm">
                        {overdueInvoices.length} vencidas
                      </Badge>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody className="px-0 pt-0 pb-2">
                  <div className="table-responsive p-0">
                    <Table className="align-items-center mb-0" hover>
                      <thead>
                        <tr>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Factura
                          </th>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                            Cliente
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Monto
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Vencimiento
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Días Vencido
                          </th>
                          <th className="text-secondary opacity-7"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {overdueInvoices.map((invoice) => (
                          <tr 
                            key={invoice.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => viewInvoiceDetail(invoice.id)}
                          >
                            {/* Factura */}
                            <td>
                              <div className="d-flex px-2 py-1">
                                <div>
                                  <div className="icon icon-shape icon-sm bg-gradient-danger shadow text-center rounded-circle me-2">
                                    <i className="ni ni-single-copy-04 text-white text-xs"></i>
                                  </div>
                                </div>
                                <div className="d-flex flex-column justify-content-center">
                                  <h6 className="mb-0 text-sm font-weight-bold text-dark">
                                    {invoice.folio || 'Sin folio'}
                                  </h6>
                                </div>
                              </div>
                            </td>

                            {/* Cliente */}
                            <td>
                              <p className="text-sm mb-0 text-dark">
                                {invoice.client_name || 'N/A'}
                              </p>
                            </td>

                            {/* Monto */}
                            <td>
                              <p className="text-sm font-weight-bold mb-0 text-dark">
                                  {formatCurrency(invoice.subtotal)}
                              </p>
                              <small className="text-xs text-muted">
                                + IVA = {formatCurrency(invoice.total)}
                              </small>
                            </td>

                            {/* Vencimiento */}
                            <td className="align-middle text-center">
                              <span className="text-secondary text-xs font-weight-bold">
                                {formatDate(invoice.due_date)}
                              </span>
                            </td>

                            {/* Días Vencido */}
                            <td className="align-middle text-center">
                              <Badge color="" className="badge-sm bg-gradient-danger shadow-sm">
                                <i className="ni ni-time-alarm me-1"></i>
                                {invoice.days_overdue || 0} días
                              </Badge>
                            </td>

                            {/* Acciones */}
                            <td className="align-middle text-center">
                              <Button
                                color="link"
                                size="sm"
                                className="text-dark font-weight-bold text-xs me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewInvoiceDetail(invoice.id);
                                }}
                              >
                                Ver
                              </Button>
                              <Button
                                color="primary"
                                size="sm"
                                className="btn-icon shadow-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkPaid(invoice.id);
                                }}
                              >
                                <span className="btn-inner--icon">
                                  Marcar como pagado 
                                </span>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ============================================ */}
        {/* FACTURAS POR COBRAR */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="pb-0 border-0">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0">
                      <i className="ni ni-bullet-list-67 me-2"></i>
                      Facturas por Cobrar
                    </h6>
                    <p className="text-xs text-muted mb-0 mt-1">
                      Estado de facturas pendientes de pago
                    </p>
                  </Col>
                  <Col md="6" className="text-end">
                    <Badge color="" className="badge-lg bg-gradient-info shadow-sm">
                      {pendingInvoices.length} pendientes
                    </Badge>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0" hover>
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Factura
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Cliente
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Orden
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Monto
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Emisión
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Vencimiento
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Estado
                        </th>
                        <th className="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingInvoices.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <i className="ni ni-like-2 text-success" style={{ fontSize: '3rem' }}></i>
                            <h6 className="mt-3 text-dark">No hay facturas pendientes de cobro</h6>
                            <p className="text-xs text-secondary mb-0 mt-2">
                              Todas las facturas están al corriente
                            </p>
                          </td>
                        </tr>
                      ) : (
                        pendingInvoices.map((invoice) => {
                          const isOverdue = invoice.is_overdue;
                          const daysUntilDue = invoice.days_until_due;
                          
                          // Determinar color del badge según estado
                          let statusBadge;
                          if (isOverdue) {
                            statusBadge = { color: 'danger', text: `Vencida (${Math.abs(daysUntilDue)} días)`, icon: 'ni-fat-remove' };
                          } else if (daysUntilDue <= 3) {
                            statusBadge = { color: 'warning', text: `Por vencer (${daysUntilDue} días)`, icon: 'ni-time-alarm' };
                          } else {
                            statusBadge = { color: 'info', text: `Pendiente (${daysUntilDue} días)`, icon: 'ni-calendar-grid-58' };
                          }
                          
                          return (
                            <tr 
                              key={invoice.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => viewInvoiceDetail(invoice.id)}
                            >
                              {/* Factura */}
                              <td>
                                <div className="d-flex px-2 py-1">
                                  <div>
                                    <div className={`icon icon-shape icon-sm bg-gradient-${statusBadge.color} shadow text-center rounded-circle me-2`}>
                                      <i className="ni ni-single-copy-04 text-white text-xs"></i>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm font-weight-bold text-dark">
                                      {invoice.folio || 'Sin folio'}
                                    </h6>
                                  </div>
                                </div>
                              </td>

                              {/* Cliente */}
                              <td>
                                <p className="text-sm mb-0 text-dark">
                                  {invoice.client_name || 'N/A'}
                                </p>
                              </td>

                              {/* Orden */}
                              <td>
                                <p className="text-sm mb-0 text-dark">
                                  {invoice.order_number || 'N/A'}
                                </p>
                              </td>

                              {/* Monto */}
                              <td>
                                  <p className="text-sm font-weight-bold mb-0 text-dark">
                                    {formatCurrency(invoice.subtotal)}
                                  </p>
                                  <small className="text-xs text-muted">
                                    + IVA = {formatCurrency(invoice.total)}
                                  </small>
                                </td>

                              {/* Emisión */}
                              <td className="align-middle text-center">
                                <span className="text-secondary text-xs font-weight-bold">
                                  {formatDate(invoice.issue_date)}
                                </span>
                              </td>

                              {/* Vencimiento */}
                              <td className="align-middle text-center">
                                <span className="text-secondary text-xs font-weight-bold">
                                  {formatDate(invoice.due_date)}
                                </span>
                              </td>

                              {/* Estado */}
                              <td className="align-middle text-center">
                                <Badge color="" className={`badge-sm bg-gradient-primary shadow-sm`}>
                                  <i className={`${statusBadge.icon} me-1`}></i>
                                  {statusBadge.text}
                                </Badge>
                              </td>

                              {/* Acciones */}
                              <td className="align-middle text-center">
                                <Button
                                  color="link"
                                  size="sm"
                                  className="text-dark font-weight-bold text-xs me-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    viewInvoiceDetail(invoice.id);
                                  }}
                                >
                                  Ver
                                </Button>
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="btn-icon shadow-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkPaid(invoice.id);
                                  }}
                                >
                                  <span className="btn-inner--icon">
                                    Marcar como pagado
                                  </span>
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ============================================ */}
      {/* MODAL DE DETALLE DE FACTURA */}
      {/* ============================================ */}
      <InvoiceViewerModal
        isOpen={viewerModalOpen}
        toggle={() => setViewerModalOpen(false)}
        invoiceId={selectedInvoiceId}
      />
    </>
  );
};

export default CashflowDashboard;
